import { Observable } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { LogService } from '../../util/log.service';
import { API_ENDPOINTS, ApiService } from '../api.service';
import { GenericApiClientService } from '../generic-api-client.service';
import { IApiQueryOptions } from '../models/api-query-params.model';
import { IDeviceModel } from './device-model.interface';

@Injectable({
  providedIn: 'root'
})
export class DeviceClientService extends GenericApiClientService<IDeviceModel> {
  constructor(http: HttpClient, apiService: ApiService, log: LogService) {
    super(http, apiService, log);
    this.itemsUrl = apiService.getUrlFor(API_ENDPOINTS.devices);
    this.singleItemUrl = apiService.getUrlFor(API_ENDPOINTS.device);
  }

  /**
   * Get devices for a specific flat
   * @param flatId The flat ID to get devices for
   * @param options Query options for filtering, ordering, and pagination. Wanna filter for device types? Use the deviceType.id={id} query param. Check the hardcoded config for device type constants.
   * @returns Observable of paginated devices
   */
  getFlatDevices(flatId: number, options?: IApiQueryOptions): Observable<IDeviceModel[]> {
    const url = this.apiService.getUrlFor(API_ENDPOINTS.flats) + '/' + flatId + '/devices';
    return this.getAllItems(url, options);
  }

  /**
   * Get all devices for a specific device type
   * @param deviceTypeId The device type ID to get devices for
   * @param options Query options for ordering and pagination (filtering by device type is already done by the endpoint)
   * @returns Observable of all devices for the specified device type
   */
  getDevicesByType(deviceTypeId: number, options?: Omit<IApiQueryOptions, 'filters'>): Observable<IDeviceModel[]> {
    const url = this.apiService.getUrlFor(API_ENDPOINTS.deviceTypes) + '/' + deviceTypeId + '/devices';
    return this.getAllItems(url, options);
  }

  /**
   * Get devices with flexible filtering options
   * Supports filtering by device properties and nested relations
   * 
   * Available filters:
   * - id (exact match)
   * - name (partial match)
   * - status (exact match)
   * - status_changed_at (partial match)
   * - updated_at (partial match)
   * - created_at (partial match)
   * - flat.id (exact match)
   * - flat.building.id (exact match)
   * - deviceType.id (exact match)
   * 
   * @example
   * // Get presence detectors for a specific flat
   * getDevices({ 'deviceType.id': 3, 'flat.id': 7 })
   * 
   * @example
   * // Get all presence detectors in a building
   * getDevices({ 'deviceType.id': 3, 'flat.building.id': 1 })
   * 
   * @param filters Filter options for querying devices
   * @param options Additional query options for ordering and pagination
   * @returns Observable of filtered devices
   */
  getDevices(
    filters?: { [key: string]: string | number | boolean | null },
    options?: Omit<IApiQueryOptions, 'filters'>
  ): Observable<IDeviceModel[]> {
    const queryOptions: IApiQueryOptions = {
      ...options,
      filters: filters || {}
    };
    return this.getAllItems(this.itemsUrl, queryOptions);
  }
}
